import { Component, inject, OnInit, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";
import {
  TableActionConfig,
  TableColumn,
  TableConfig,
  TableDateRangeEvent,
  TableFilterChangeEvent,
  TablePagination,
  TableSearchEvent,
  TableSort,
} from "../../../../Common/Components/Table/Type/TableType";
import { AppTableComponent } from "../../../../Common/Components/Table/TableComponent";
import { VendorEffects } from "../../State/VendorEffects";
import { VendorState } from "../../State/VendorState";
import { Vendor } from "../../Dto/VendorModel";
import { VendorType } from "../../Dto/VendorModel";
import { formatDateNumeric } from "../../../../Common/Utility/UtilityDate";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-vendor-home",
  standalone: true,
  imports: [AppTableComponent],
  templateUrl: "./VendorHomePage.html",
  styleUrl: "./VendorHomePage.scss",
  encapsulation: ViewEncapsulation.None,
})
export class VendorHomePage implements OnInit {
  private readonly vendorEffects = inject(VendorEffects);
  readonly vendorState = inject(VendorState);
  private readonly router = inject(Router);
  private readonly toastEffects = inject(ToastEffects);
  selectedTypeFilter = "";
  readonly vendorTypeOptions = Object.values(VendorType).map((type) => ({ value: type, label: type }));

  columns: TableColumn<Vendor>[] = [
    { key: "name", header: "Name", sortable: true, width: "16%" },
    {
      key: "type",
      header: "Type",
      sortable: false,
      width: "10%",
      cellTemplate: (item) => item.type,
      cellClass: (item) => this.getTypeChipClass(item.type),
      cellIcon: (item) => this.getTypeChipIcon(item.type),
    },
    { key: "contactPerson", header: "Contact", sortable: false, width: "12%" },
    { key: "phone", header: "Phone", sortable: false, width: "12%" },
    { key: "email", header: "Email", sortable: false, width: "16%" },
    {
      key: "address",
      header: "Address",
      sortable: false,
      width: "22%",
      cellTemplate: (item) => this.formatAddress(item),
    },
    {
      key: "updatedAt",
      header: "Updated",
      sortable: true,
      width: "12%",
      cellTemplate: (item) =>
        (() => {
          if (item.updatedAt) {
            return formatDateNumeric(item.updatedAt);
          }
          return "-";
        })(),
    },
  ];

  get config(): TableConfig<Vendor> {
    return {
      loading: this.vendorState.loading(),
      emptyMessage: this.vendorState.error(),
      showActions: true,
      showSearch: true,
      searchPlaceholder: "Search vendors...",
      showCreate: true,
      createButtonLabel: "Create Vendor",
      createButtonIcon: "add",
      showDateRangeFilter: true,
      dateRangeFilterLabel: "Filter by creation date",
      filters: [
        {
          key: "type",
          label: "Type",
          placeholder: "All types",
          options: this.vendorTypeOptions,
          defaultValue: this.selectedTypeFilter,
        },
      ],
      onCreate: () => this.router.navigate(["/vendors/create"]),
      actionButtons: [
        { label: "edit", icon: "edit", handler: (item) => this.router.navigate(["/vendors/edit", item.id]) },
        { label: "delete", icon: "delete", handler: (item) => this.delete(item) },
      ],
    };
  }

  get pagination(): TablePagination {
    return this.vendorState.pagination();
  }

  ngOnInit(): void {
    const current = this.vendorState.searchParams();
    this.selectedTypeFilter = current.type ?? "";
    this.vendorEffects.searchVendors(current);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSortChange(sort: TableSort | null): void {
    return;
  }

  onPageChange(event: { page: number; search?: string }): void {
    const current = this.vendorState.searchParams();
    this.vendorEffects.searchVendors({ ...current, page: event.page, search: event.search });
  }

  onPageSizeChange(event: { limit: number; search?: string }): void {
    const current = this.vendorState.searchParams();
    this.vendorEffects.searchVendors({ ...current, page: 1, limit: event.limit, search: event.search });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onActionClick<T>(event: { action: TableActionConfig<T>; item: T }): void {
    return;
  }

  onSearchChange(event: TableSearchEvent): void {
    const current = this.vendorState.searchParams();
    this.vendorEffects.searchVendors({
      ...current,
      search: event.term,
      page: event.page,
    });
  }

  onFilterChange(event: TableFilterChangeEvent): void {
    this.selectedTypeFilter = event.filters["type"] ?? "";

    const current = this.vendorState.searchParams();
    const nextType = this.selectedTypeFilter.trim() as VendorType | "";
    this.vendorEffects.searchVendors({
      ...current,
      type: nextType || undefined,
      page: 1,
    });
  }

  onDateRangeChange(event: TableDateRangeEvent): void {
    const current = this.vendorState.searchParams();
    this.vendorEffects.searchVendors({
      ...current,
      page: event.page,
      createdAtFrom: event.dateRange.from,
      createdAtTo: event.dateRange.to,
    });
  }

  async delete(item: Vendor): Promise<void> {
    try {
      await this.vendorEffects.deleteVendor(item.id);
      this.toastEffects.show("Vendor deleted.", "success");
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to delete vendor."), "error");
    }
  }

  private formatAddress(item: Vendor): string {
    if (!item.address) {
      return "-";
    }

    if (typeof item.address === "string") {
      return item.address.trim() || "-";
    }

    const parts = [
      item.address.stateProvinceRegion,
      item.address.countyDistrict,
      item.address.subdistrictMunicipality,
      item.address.communeVillage,
      item.address.line,
      item.address.zip,
    ]
      .map((part) => (part ?? "").trim())
      .filter((part) => part.length > 0);

    return (() => {
      if (parts.length > 0) {
        return parts.join(", ");
      }
      return "-";
    })();
  }

  private getTypeChipClass(type: VendorType): string {
    if (type === VendorType.Printing) {
      return "app-table__chip app-table__chip--info";
    }

    return "app-table__chip app-table__chip--warning";
  }

  private getTypeChipIcon(type: VendorType): string {
    if (type === VendorType.Printing) {
      return "print";
    }

    return "local_shipping";
  }
}
